import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toast';
import axiosInstance from '../../api/axiosInstance';
import '../../styles/project-test-plan.css';

// Enums
const PRIORITY_ENUM = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3
};

const PRIORITY_LABELS = {
  1: 'Low',
  2: 'Medium',
  3: 'High'
};

const STATUS_ENUM = {
  NOT_STARTED: 1,
  IN_PROGRESS: 2,
  COMPLETED: 3,
  ON_HOLD: 4,
  CANCELLED: 5
};

const STATUS_LABELS = {
  1: 'Not Started',
  2: 'In Progress',
  3: 'Completed',
  4: 'On Hold',
  5: 'Cancelled'
};

// Dummy QA Members - will be replaced with API call
const DUMMY_QA_MEMBERS = [];

// Dummy Project Owners
const DUMMY_PROJECT_OWNERS = [
  { id: 1, name: 'Manager One' },
  { id: 2, name: 'Manager Two' },
  { id: 3, name: 'Manager Three' }
];

const ProjectTestPlan = () => {
  const navigate = useNavigate();
  const { project_id } = useParams();
  const [searchParams] = useSearchParams();
  const projectIdFromParams = searchParams.get('project_id') || project_id;
  const modeParam = searchParams.get('mode');
  const isEditMode = modeParam === 'edit';
  const testPlanId = project_id; // In edit mode, project_id is the test plan's project_id

  const { control, handleSubmit, watch, formState: { errors }, setValue, getValues } = useForm({
    defaultValues: {
      projectName: '',
      description: '',
      scope: '',
      outOfScope: '',
      startDate: '',
      endDate: '',
      projectOwner: '',
      priority: PRIORITY_ENUM.MEDIUM,
      status: STATUS_ENUM.NOT_STARTED,
      modules: [],
      qaMembers: [],
      attachment: null
    }
  });

  const [projects, setProjects] = useState([]);
  const [qaMembers, setQaMembers] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [qaLoading, setQaLoading] = useState(false);
  const [originalAttachmentPath, setOriginalAttachmentPath] = useState(null);
  const [isFileModified, setIsFileModified] = useState(false);
  const [uploadedFilePath, setUploadedFilePath] = useState(null);
  const [moduleInput, setModuleInput] = useState({ name: '', description: '', startDate: '', endDate: '', status: STATUS_ENUM.NOT_STARTED });
  const [featureInput, setFeatureInput] = useState({ name: '', description: '' });
  const [expandedModuleIndex, setExpandedModuleIndex] = useState(null);
  const [qaSearchTerm, setQaSearchTerm] = useState('');
  const [showQaDropdown, setShowQaDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentModules = watch('modules');
  const currentQaMembers = watch('qaMembers');
  const currentAttachment = watch('attachment');
  const selectedProjectId = watch('projectName');
  const startDateValue = watch('startDate');
  const endDateValue = watch('endDate');

  // Fetch projects and QA members
  useEffect(() => {
    const fetchProjects = async () => {
      setProjectsLoading(true);
      try {
        const response = await axiosInstance.get(
          `/projects/list_project?page=1&page_size=100&pro_status=active`
        );
        if (response.projects) {
          setProjects(response.projects);
          
          // Prefill project if provided in params, otherwise use default (1)
          const projectToSet = projectIdFromParams || 1;
          const foundProject = response.projects.find(p => p.project_id === parseInt(projectToSet) || p.project_id === projectToSet);
          if (foundProject) {
            setValue('projectName', foundProject.project_id);
            if(!isEditMode){
            fetchQaMembers(foundProject.project_id);
            }
            // Fetch QA members for the selected project
          }
        }
      } catch (error) {
        toast.error('Failed to load projects');
        console.error('Error fetching projects:', error);
      } finally {
        setProjectsLoading(false);
      }
    };

    fetchProjects();
  }, [projectIdFromParams, setValue]);

  // Fetch QA Members for selected project
  const fetchQaMembers = async (projectId) => {
    setQaLoading(true);
    try {
      const response = await axiosInstance.get(
        `/test_plan_document/list_qa_members?project_id=${projectId}`
      );
      if (response.data && Array.isArray(response.data)) {
        setQaMembers(response.data);
      }
    } catch (error) {
      toast.error('Failed to load QA members');
      console.error('Error fetching QA members:', error);
      setQaMembers([]);
    } finally {
      setQaLoading(false);
    }
  };

  // Handle project change to refetch QA members
  const handleProjectChange = (newProjectId) => {
    setValue('projectName', newProjectId);
    setValue('qaMembers', []); // Reset selected QA members
    if (newProjectId) {
      fetchQaMembers(newProjectId);
    }
  };

  // Add Module
  const handleAddModule = () => {
    if (!moduleInput.name.trim()) {
      toast.error('Module name is required');
      return;
    }
    if (moduleInput.name.length > 64) {
      toast.error('Module name must not exceed 64 characters');
      return;
    }
    if (moduleInput.description.length > 256) {
      toast.error('Module description must not exceed 256 characters');
      return;
    }
    if (!moduleInput.startDate) {
      toast.error('Module start date is required');
      return;
    }
    if (!moduleInput.endDate) {
      toast.error('Module end date is required');
      return;
    }
    if (new Date(moduleInput.startDate) > new Date(moduleInput.endDate)) {
      toast.error('Module start date cannot be greater than end date');
      return;
    }
    // Validate module dates are within project dates
    if (startDateValue && new Date(moduleInput.startDate) < new Date(startDateValue)) {
      toast.error('Module start date cannot be before project start date');
      return;
    }
    if (endDateValue && new Date(moduleInput.endDate) > new Date(endDateValue)) {
      toast.error('Module end date cannot be after project end date');
      return;
    }

    const newModule = {
      id: Date.now(),
      name: moduleInput.name,
      description: moduleInput.description,
      startDate: moduleInput.startDate,
      endDate: moduleInput.endDate,
      status: moduleInput.status,
      features: []
    };

    setValue('modules', [...currentModules, newModule]);
    setModuleInput({ name: '', description: '', startDate: '', endDate: '', status: STATUS_ENUM.NOT_STARTED });
    toast.success('Module added successfully');
  };

  // Delete Module
  const handleDeleteModule = (moduleId) => {
    setValue('modules', currentModules.filter(m => m.id !== moduleId));
    setExpandedModuleIndex(null);
    toast.success('Module deleted successfully');
  };

  // Add Feature to Module
  const handleAddFeature = (moduleId) => {
    if (!featureInput.name.trim()) {
      toast.error('Feature name is required');
      return;
    }
    if (featureInput.name.length > 64) {
      toast.error('Feature name must not exceed 64 characters');
      return;
    }
    if (featureInput.description.length > 256) {
      toast.error('Feature description must not exceed 256 characters');
      return;
    }

    const updatedModules = currentModules.map(module => {
      if (module.id === moduleId) {
        return {
          ...module,
          features: [
            ...module.features,
            {
              id: Date.now(),
              name: featureInput.name,
              description: featureInput.description
            }
          ]
        };
      }
      return module;
    });

    setValue('modules', updatedModules);
    setFeatureInput({ name: '', description: '' });
    toast.success('Feature added successfully');
  };

  // Delete Feature
  const handleDeleteFeature = (moduleId, featureId) => {
    const updatedModules = currentModules.map(module => {
      if (module.id === moduleId) {
        return {
          ...module,
          features: module.features.filter(f => f.id !== featureId)
        };
      }
      return module;
    });

    setValue('modules', updatedModules);
    toast.success('Feature deleted successfully');
  };

  // Handle QA Member Selection
  const handleQaMemberSelect = (member) => {
    if (!currentQaMembers.find(m => m.user_id === member.user_id)) {
      setValue('qaMembers', [...currentQaMembers, member]);
    }
    setQaSearchTerm('');
    setShowQaDropdown(false);
  };

  // Remove QA Member
  const handleRemoveQaMember = (memberId) => {
    setValue('qaMembers', currentQaMembers.filter(m => m.user_id !== memberId));
  };

  // Filter QA Members
  const filteredQaMembers = qaMembers.filter(
    member =>
      !currentQaMembers.find(m => m.user_id === member.user_id) &&
      member.name.toLowerCase().includes(qaSearchTerm.toLowerCase())
  );

  // Handle File Upload
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;

    const file = files[0]; // Only accept single file

    // Validate file type (PDF or images)
    const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only PDF and image files (PNG, JPG, GIF) are allowed');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`${file.name} exceeds 5MB limit`);
      return;
    }

    // Create file object with preview
    const reader = new FileReader();
    reader.onload = (event) => {
      const newAttachment = {
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        preview: event.target.result,
        file: file,
        isPdf: file.type === 'application/pdf'
      };

      // Remove previous attachment and add new one
      setValue('attachment', newAttachment);
      setIsFileModified(true); // Mark that file has been changed
      setUploadedFilePath(null); // Reset uploaded path
      toast.success(`${file.name} uploaded successfully`);
    };
    reader.readAsDataURL(file);
  };

  // Remove Attachment
  const handleRemoveAttachment = () => {
    setValue('attachment', null);
    setIsFileModified(true); // Mark that file has been changed
    setUploadedFilePath(null); // Reset uploaded path
  };

  // Upload file to S3
  const uploadFileToS3 = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axiosInstance.post(
        '/test_plan_document/upload_file',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          skipGlobalToast: true,
        }
      );

      if (response.status_code === 200 && response.file_path) {
        return response.file_path;
      }
      throw new Error('File upload failed');
    } catch (error) {
      toast.error('Failed to upload file');
      console.error('File upload error:', error);
      throw error;
    }
  };

  // Create test plan
  const createTestPlan = async (payload) => {
    try {
      const response = await axiosInstance.post(
        '/test_plan_document/save_test_plan_details',
        payload,
        { skipGlobalToast: true }
      );

      if (response.status_code === 200) {
        toast.success('Test plan created successfully');
        return true;
      }
      throw new Error(response.message || 'Failed to create test plan');
    } catch (error) {
      toast.error('Failed to create test plan');
      console.error('Create test plan error:', error);
      throw error;
    }
  };

  // Update test plan
  const updateTestPlan = async (payload) => {
    try {
      const response = await axiosInstance.put(
        '/test_plan_document/update_test_plan_details',
        payload,
        { skipGlobalToast: true }
      );

      if (response.status_code === 200) {
        toast.success('Test plan updated successfully');
        return true;
      }
      throw new Error(response.message || 'Failed to update test plan');
    } catch (error) {
      toast.error('Failed to update test plan');
      console.error('Update test plan error:', error);
      throw error;
    }
  };

  // Fetch test plan details for edit mode
  const fetchTestPlanDetails = async (projectIdForApi) => {
    try {
      const response = await axiosInstance.get(
        `/test_plan_document/get_test_plan_details?project_id=${projectIdForApi}`
      );

      if (response.status_code === 200 && response.data) {
        const data = response.data;
        
        // Prefill form fields
        setValue('projectName', data.project_id);
        setValue('description', data.description || '');
        setValue('scope', data.scope || '');
        setValue('outOfScope', data.out_of_scope || '');
        setValue('startDate', data.start_date || '');
        setValue('endDate', data.end_date || '');
        setValue('priority', data.priority || PRIORITY_ENUM.MEDIUM);
        setValue('status', data.plan_status || STATUS_ENUM.NOT_STARTED);

        // Prefill modules
        if (data.modules && Array.isArray(data.modules)) {
          const transformedModules = data.modules.map(m => ({
            id: m.id,
            name: m.name,
            description: m.description,
            startDate: m.start_date,
            endDate: m.end_date,
            status: m.module_status,
            features: (m.features || []).map(f => ({
              id: f.id,
              name: f.feature_name,
              description: f.description
            }))
          }));
          setValue('modules', transformedModules);
        }

        // Fetch and match QA members
        if (data.project_id) {
          try {
            const qaResponse = await axiosInstance.get(
              `/test_plan_document/list_qa_members?project_id=${data.project_id}`
            );
            if (qaResponse.data && Array.isArray(qaResponse.data)) {
              const fetchedQaMembers = qaResponse.data;
              setQaMembers(fetchedQaMembers);
              setValue('projectOwner', data.project_owner_id || '');
              // Match QA members from API response
              if (data.qa_members && Array.isArray(data.qa_members)) { 
                const matchedMembers = fetchedQaMembers.filter(m => 
                  data.qa_members.includes(m.user_id)
                );
                setValue('qaMembers', matchedMembers); 
              }
            }
          } catch (error) {
            console.error('Error fetching QA members for edit mode:', error);
          }
        }

        // Store original attachment path
        if (data.attachment) {
          setOriginalAttachmentPath(data.attachment);
          // Determine file type from the URL or path
          const isPdf = data.attachment.toLowerCase().endsWith('.pdf');
          const fileType = isPdf ? 'application/pdf' : 'image/*';
          const fileName = data.attachment.split('/').pop() || 'attachment';
          
          const newAttachment = {
            id: Date.now() + Math.random(),
            preview: data.attachment,
            name: fileName,
            size: 0, // Size not available from API, set to 0
            type: fileType,
            isPdf: isPdf,
            file: null // File object not available from API
          };
          setValue('attachment', newAttachment);

        }
      }
      
    } catch (error) {
      toast.error('Failed to load test plan details');
      console.error('Fetch test plan details error:', error);
    }
  };

  // Transform modules for API
  const transformModulesForApi = (modules) => {
    return modules.map(m => ({
      module_name: m.name,
      description: m.description,
      start_date: m.startDate,
      end_date: m.endDate,
      status: m.status,
      features: (m.features || []).map(f => ({
        feature_name: f.name,
        feature_description: f.description
      }))
    }));
  };

  // Load test plan in edit mode
  useEffect(() => {
    if (isEditMode && testPlanId) {
      // fetchQaMembers(testPlanId);
      fetchTestPlanDetails(testPlanId)
    }
  }, [isEditMode, testPlanId]);

  // Form Submission
  const onSubmit = async (data) => {
    // Validate dates
    if (startDateValue && endDateValue) {
      if (new Date(startDateValue) > new Date(endDateValue)) {
        toast.error('Start date cannot be greater than end date');
        return;
      }
    }

    // Validate required fields
    if (!data.projectName) {
      toast.error('Please select a project');
      return;
    }
    if (!data.projectOwner) {
      toast.error('Please select a project owner');
      return;
    }
    if (!data.scope) {
      toast.error('Scope is required');
      return;
    }
    if (!data.outOfScope) {
      toast.error('Out-Of-Scope is required');
      return;
    }

    setIsSubmitting(true);

    try {
      let filePath = null;

      // Step 1: Upload file if it exists and has been modified
      if (currentAttachment && currentAttachment.file && isFileModified) {
        try {
          filePath = await uploadFileToS3(currentAttachment.file);
          setUploadedFilePath(filePath);
        } catch (error) {
          // File upload failed but keep the path for retry
          setIsSubmitting(false);
          return;
        }
      } else if (!isFileModified && originalAttachmentPath) {
        // Use original attachment path if file hasn't been modified in edit mode
        filePath = originalAttachmentPath;
      }

      // Step 2: Transform modules for API
      const transformedModules = transformModulesForApi(data.modules);

      // Step 3: Prepare payload
      const payload = {
        project_id: parseInt(data.projectName),
        description: data.description,
        scope: data.scope,
        out_of_scope: data.outOfScope,
        start_date: data.startDate,
        end_date: data.endDate,
        priority: parseInt(data.priority),
        status: parseInt(data.status),
        project_owner_id: parseInt(data.projectOwner),
        attachment_path: filePath || null,
        modules: transformedModules,
        qa_members: data.qaMembers.map(m => m.user_id)
      };

      console.log('Payload to submit:', payload);

      // Step 4: Create or Update test plan
      if (isEditMode) {
        await updateTestPlan(payload);
      } else {
        await createTestPlan(payload);
      }

      // Step 5: Navigate on success
      navigate('/dashboard/projects');

    } catch (error) {
      console.error('Error submitting form:', error);
      // Error toast already shown by API functions
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedProject = projects.find(p => p.project_id === selectedProjectId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">{isEditMode ? 'Update Test Plan' : 'Project Setup'}</h1>
          <p className="text-slate-600">{isEditMode ? 'Edit and update your test plan details' : 'Create and manage your project details efficiently'}</p>
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-6 sm:p-8">
            {/* Project Details Section */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-3 border-b border-slate-200">
                Project Details
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Project Name Dropdown */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Project Name <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="projectName"
                    control={control}
                    rules={{ required: 'Project is required' }}
                    render={({ field }) => (
                      <select
                        {...field}
                        disabled={projectsLoading || isEditMode}
                        onChange={(e) => handleProjectChange(e.target.value)}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          errors.projectName ? 'border-red-500' : 'border-slate-300'
                        } ${projectsLoading || isEditMode ? 'bg-slate-100 cursor-not-allowed' : 'bg-white'}`}
                      >
                        <option value="">Select a project</option>
                        {projects.map(project => (
                          <option key={project.project_id} value={project.project_id}>
                            {project.project_name}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.projectName && (
                    <p className="text-red-500 text-sm mt-1">{errors.projectName.message}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    Description
                  </label>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        maxLength="256"
                        placeholder="Enter project description"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                        rows="3"
                      />
                    )}
                  />
                  {/* <p className="text-xs text-slate-500 mt-1">
                    {getValues('description').length}/256 characters
                  </p> */}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Scope */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Scope <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="scope"
                    control={control}
                    rules={{ required: 'Scope is required' }}
                    render={({ field }) => (
                      <textarea
                        {...field}
                        placeholder="Enter project scope"
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          errors.scope ? 'border-red-500' : 'border-slate-300'
                        }`}
                        rows="3"
                      />
                    )}
                  />
                  {errors.scope && (
                    <p className="text-red-500 text-sm mt-1">{errors.scope.message}</p>
                  )}
                </div>

                {/* Out-Of-Scope */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Out-Of-Scope <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="outOfScope"
                    control={control}
                    rules={{ required: 'Out-Of-Scope is required' }}
                    render={({ field }) => (
                      <textarea
                        {...field}
                        placeholder="Enter what's out of scope"
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          errors.outOfScope ? 'border-red-500' : 'border-slate-300'
                        }`}
                        rows="3"
                      />
                    )}
                  />
                  {errors.outOfScope && (
                    <p className="text-red-500 text-sm mt-1">{errors.outOfScope.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Start Date */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="startDate"
                    control={control}
                    rules={{
                      required: 'Start date is required',
                      validate: (value) => {
                        if (endDateValue && value && new Date(value) > new Date(endDateValue)) {
                          return 'Start date cannot be greater than end date';
                        }
                        return true;
                      }
                    }}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="date"
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          errors.startDate ? 'border-red-500' : 'border-slate-300'
                        }`}
                      />
                    )}
                  />
                  {errors.startDate && (
                    <p className="text-red-500 text-sm mt-1">{errors.startDate.message}</p>
                  )}
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="endDate"
                    control={control}
                    rules={{
                      required: 'End date is required',
                      validate: (value) => {
                        if (startDateValue && value && new Date(value) < new Date(startDateValue)) {
                          return 'End date cannot be less than start date';
                        }
                        return true;
                      }
                    }}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="date"
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          errors.endDate ? 'border-red-500' : 'border-slate-300'
                        }`}
                      />
                    )}
                  />
                  {errors.endDate && (
                    <p className="text-red-500 text-sm mt-1">{errors.endDate.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Project Owner */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Project Owner <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="projectOwner"
                    control={control}
                    rules={{ required: 'Project owner is required' }}
                    render={({ field }) => (
                      <select
                        {...field}
                        disabled={isEditMode || qaLoading}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          errors.projectOwner ? 'border-red-500' : 'border-slate-300'
                        } ${isEditMode || qaLoading ? 'bg-slate-100 cursor-not-allowed' : 'bg-white'}`}
                      >
                        <option value="">Select owner</option>
                        {qaMembers.map(member => (
                          <option key={member.user_id} value={member.user_id}>
                            {member.name}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.projectOwner && (
                    <p className="text-red-500 text-sm mt-1">{errors.projectOwner.message}</p>
                  )}
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Priority <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="priority"
                    control={control}
                    render={({ field }) => (
                      <select
                        {...field}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      >
                        {Object.entries(PRIORITY_ENUM).map(([key, value]) => (
                          <option key={value} value={value}>
                            {PRIORITY_LABELS[value]}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <select
                        {...field}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      >
                        {Object.entries(STATUS_ENUM).map(([key, value]) => (
                          <option key={value} value={value}>
                            {STATUS_LABELS[value]}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Modules and Features Section */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-3 border-b border-slate-200">
                Modules and Features
              </h2>

              {/* Add Module Form */}
              <div className="bg-slate-50 p-6 rounded-lg mb-8 border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Add Module</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <input
                      type="text"
                      maxLength="64"
                      value={moduleInput.name}
                      onChange={(e) => setModuleInput({ ...moduleInput, name: e.target.value })}
                      placeholder="Module Name"
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {moduleInput.name.length}/64 characters
                    </p>
                  </div>
                  <div>
                    <input
                      maxLength="256"
                      type="text"
                      value={moduleInput.description}
                      onChange={(e) => setModuleInput({ ...moduleInput, description: e.target.value })}
                      placeholder="Module Description"
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                      rows="2"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {moduleInput.description.length}/256 characters
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={moduleInput.startDate}
                      onChange={(e) => setModuleInput({ ...moduleInput, startDate: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={moduleInput.endDate}
                      onChange={(e) => setModuleInput({ ...moduleInput, endDate: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={moduleInput.status}
                      onChange={(e) => setModuleInput({ ...moduleInput, status: parseInt(e.target.value) })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                    >
                      <option value={STATUS_ENUM.NOT_STARTED}>Not Started</option>
                      <option value={STATUS_ENUM.IN_PROGRESS}>In Progress</option>
                      <option value={STATUS_ENUM.COMPLETED}>Completed</option>
                      <option value={STATUS_ENUM.ON_HOLD}>On Hold</option>
                      <option value={STATUS_ENUM.CANCELLED}>Cancelled</option>
                    </select>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleAddModule}
                  className="w-full md:w-auto px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all"
                >
                  + Add Module
                </button>
              </div>

              {/* Modules Table */}
              {currentModules.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-slate-100 border-b border-slate-300">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Module Name</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Description</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Dates</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Features</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentModules.map((module, moduleIndex) => (
                        <React.Fragment key={module.id}>
                          <tr className="border-b border-slate-200 hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-4 text-sm text-slate-900 font-medium">{module.name}</td>
                            <td className="px-4 py-4 text-sm text-slate-600">{module.description || '-'}</td>
                            <td className="px-4 py-4 text-sm text-slate-600">
                              {module.startDate && module.endDate ? (
                                <span>{module.startDate} to {module.endDate}</span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-4 text-sm">
                              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                                module.status === STATUS_ENUM.COMPLETED ? 'bg-green-100 text-green-800' :
                                module.status === STATUS_ENUM.IN_PROGRESS ? 'bg-yellow-100 text-yellow-800' :
                                module.status === STATUS_ENUM.ON_HOLD ? 'bg-orange-100 text-orange-800' :
                                module.status === STATUS_ENUM.CANCELLED ? 'bg-red-100 text-red-800' :
                                'bg-slate-100 text-slate-800'
                              }`}>
                                {
                                  module.status === STATUS_ENUM.COMPLETED ? 'Completed' :
                                  module.status === STATUS_ENUM.IN_PROGRESS ? 'In Progress' :
                                  module.status === STATUS_ENUM.ON_HOLD ? 'On Hold' :
                                  module.status === STATUS_ENUM.CANCELLED ? 'Cancelled' :
                                  'Not Started'
                                }
                              </span>
                            </td>
                            <td className="px-4 py-4 text-sm">
                              <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold">
                                {module.features.length} feature{module.features.length !== 1 ? 's' : ''}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-center">
                              <button
                                type="button"
                                onClick={() => setExpandedModuleIndex(expandedModuleIndex === moduleIndex ? null : moduleIndex)}
                                className="text-blue-600 hover:text-blue-700 font-semibold text-sm mr-3"
                              >
                                {expandedModuleIndex === moduleIndex ? 'Hide' : 'Show'} Features
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteModule(module.id)}
                                className="text-red-600 hover:text-red-700 font-semibold text-sm"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>

                          {/* Expanded Features Section */}
                          {expandedModuleIndex === moduleIndex && (
                            <tr className="bg-slate-50 border-b border-slate-200">
                              <td colSpan="12" className="px-4 py-6">
                                {/* Add Feature Form */}
                                <div className="bg-white p-4 rounded-lg border border-slate-200 mb-4">
                                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Add Feature</h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                    <input
                                      type="text"
                                      maxLength="64"
                                      value={featureInput.name}
                                      onChange={(e) => setFeatureInput({ ...featureInput, name: e.target.value })}
                                      placeholder="Feature Name"
                                      className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm resize-none"
                                    />
                                    <input
                                      maxLength=" 256"
                                      value={featureInput.description}
                                      onChange={(e) => setFeatureInput({ ...featureInput, description: e.target.value })}
                                      placeholder="Feature Description"
                                      className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm resize-none"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleAddFeature(module.id)}
                                    className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-all"
                                  >
                                    + Add Feature
                                  </button>
                                </div>

                                {/* Features List */}
                                {module.features.length > 0 ? (
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-semibold text-slate-700 mb-3">Features ({module.features.length})</h4>
                                    {module.features.map(feature => (
                                      <div key={feature.id} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg">
                                        <div className="flex-1">
                                          <p className="text-sm font-medium text-slate-900">{feature.name}</p>
                                          <p className="text-xs text-slate-600">{feature.description}</p>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteFeature(module.id, feature.id)}
                                          className="ml-4 text-red-600 hover:text-red-700 text-sm font-semibold"
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-slate-500 italic">No features added yet</p>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {currentModules.length === 0 && (
                <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                  <p className="text-slate-600 text-sm">No modules added yet. Create your first module above.</p>
                </div>
              )}
            </div>

            {/* QA Members Section */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-3 border-b border-slate-200">
                QA Members
              </h2>

              <div className="relative mb-4">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Select QA Members
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={qaSearchTerm}
                    onChange={(e) => {
                      setQaSearchTerm(e.target.value);
                      setShowQaDropdown(true);
                    }}
                    onFocus={() => setShowQaDropdown(true)}
                    placeholder="Search by name..."
                    disabled={qaLoading}
                    className={`w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      qaLoading ? 'bg-slate-100 cursor-not-allowed' : 'bg-white'
                    }`}
                  />

                  {/* Loading Indicator */}
                  {qaLoading && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    </div>
                  )}

                  {/* Dropdown */}
                  {showQaDropdown && !qaLoading && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-300 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                      {filteredQaMembers.length > 0 ? (
                        filteredQaMembers.map(member => (
                          <button
                            key={member.user_id}
                            type="button"
                            onClick={() => handleQaMemberSelect(member)}
                            className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-b-0"
                          >
                            <p className="text-sm font-medium text-slate-900">{member.name}</p>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-center text-sm text-slate-500">
                          {qaSearchTerm ? 'No members found' : 'All members are already selected'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected QA Members */}
              <div className="flex flex-wrap gap-3">
                {currentQaMembers.map(member => (
                  <div key={member.user_id} className="flex items-center gap-2 bg-blue-100 text-blue-900 px-4 py-2 rounded-full">
                    <span className="text-sm font-medium">{member.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveQaMember(member.user_id)}
                      className="text-blue-600 hover:text-blue-800 font-bold text-lg leading-none"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              {currentQaMembers.length === 0 && (
                <p className="text-sm text-slate-500 italic mt-2">No QA members selected</p>
              )}
            </div>

            {/* Attachments Section */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-3 border-b border-slate-200">
                Attachments
              </h2>

              {/* File Upload Area */}
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center mb-6 hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer relative group">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer block">
                  <svg className="mx-auto h-12 w-12 text-slate-400 group-hover:text-blue-500 transition-colors" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20a4 4 0 004 4h24a4 4 0 004-4V20" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4 20h40" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M24 4v20" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <p className="mt-2 text-sm font-semibold text-slate-900">Drag and drop your file here</p>
                  <p className="text-sm text-slate-600">or click to select a file</p>
                  <p className="text-xs text-slate-500 mt-1">PNG, JPG, GIF, PDF up to 5MB (single file)</p>
                </label>
              </div>

              {/* Uploaded File */}
              {currentAttachment && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 flex items-start gap-4">
                  {currentAttachment.isPdf ? (
                    // PDF Display
                    <div className="flex-shrink-0">
                      <svg className="h-12 w-12 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                        <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                      </svg>
                    </div>
                  ) : (
                    // Image Preview
                    <img src={currentAttachment.preview} alt={currentAttachment.name} className="flex-shrink-0 h-20 w-20 object-cover rounded border border-slate-300" />
                  )}
                  
                  {/* File Details */}
                  <div className="flex-grow">
                    <h3 className="text-sm font-semibold text-slate-900">{currentAttachment.name}</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      {(currentAttachment.size / 1024 / 1024).toFixed(2)} MB • {currentAttachment.type}
                    </p>
                  </div>
                  
                  {/* Remove Button */}
                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment()}
                    className="flex-shrink-0 px-4 py-2 bg-red-100 text-red-700 font-medium text-sm rounded-lg hover:bg-red-200 transition-colors"
                  >
                    Remove
                  </button>
                </div>
              )}

              {!currentAttachment && (
                <p className="text-sm text-slate-500 italic text-center py-8">No document uploaded yet</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-10 flex gap-4 justify-end border-t border-slate-200 pt-6">
              <button
                type="button"
                onClick={() => navigate('/projects')}
                className="px-6 py-2.5 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isSubmitting
                  ? (isEditMode ? 'Updating...' : 'Creating...')
                  : (isEditMode ? 'Update Test Execution' : 'Create Test Execution')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectTestPlan;

